import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  RefreshControl,
  Platform,
  useWindowDimensions,
  Modal,
} from "react-native";
import { collection, getDocs, query } from "firebase/firestore";
import { db } from "../../firebase"; // Adjust this import path if needed
import { AuthContext } from "../../Context/AuthContext"; // Adjust this import path if needed
import GlobalStyles from "../../components/StylenColor"; // Adjust this import path if needed
import { useFocusEffect } from "@react-navigation/native";
import {
  widthPercentageToDP as wp,
  heightPercentageToDP as hp,
} from "react-native-responsive-screen";
import { AnimatedCircularProgress } from "react-native-circular-progress"; // New Import
import { LinearGradient } from "expo-linear-gradient";
import { Ionicons } from "@expo/vector-icons";
import { useDesktopDetect, DashboardLayout, DesktopTheme, getScoreColor as getThemeScoreColor } from "../../components/web";

// Desktop Circular Gauge Card Component
const DesktopGaugeCard = ({
  department,
  score,
  isHovered,
  onHoverIn,
  onHoverOut,
  onPress,
}) => {
  const scoreColor = getThemeScoreColor(score);

  return (
    <TouchableOpacity
      style={[
        desktopStyles.gaugeCard,
        isHovered && desktopStyles.gaugeCardHover,
      ]}
      onMouseEnter={onHoverIn}
      onMouseLeave={onHoverOut}
      onPress={onPress}
      activeOpacity={0.92}
    >
      <View style={[desktopStyles.gaugeAccent, { backgroundColor: scoreColor }]} />
      <View style={desktopStyles.gaugeWrapper}>
        <AnimatedCircularProgress
          size={140}
          width={12}
          fill={score}
          tintColor={scoreColor}
          backgroundColor={DesktopTheme.colors.border}
          arcSweepAngle={270}
          rotation={225}
          lineCap="round"
        >
          {(fill) => (
            <View style={desktopStyles.scoreCenter}>
              <Text style={[desktopStyles.scoreValue, { color: scoreColor }]}>
                {Math.round(fill)}%
              </Text>
            </View>
          )}
        </AnimatedCircularProgress>
      </View>
      <Text style={desktopStyles.deptName} numberOfLines={2}>
        {department}
      </Text>
      <View style={[desktopStyles.scoreBadge, { backgroundColor: scoreColor + '20' }]}>
        <Text style={[desktopStyles.scoreBadgeText, { color: scoreColor }]}>
          {score >= 80 ? 'Excellent' : score >= 50 ? 'Average' : 'Needs Improvement'}
        </Text>
      </View>
    </TouchableOpacity>
  );
};

// Desktop View Component
const DesktopDepartmentScoresView = ({
  loading,
  departmentScores,
  selectedQuarter,
  setSelectedQuarter,
  onRefresh,
  onDepartmentPress,
}) => {
  const [hoveredCard, setHoveredCard] = useState(null);
  const [hoveredQuarter, setHoveredQuarter] = useState(null);

  // Calculate summary stats
  const avgScore = departmentScores.length > 0
    ? Math.round(departmentScores.reduce((sum, d) => sum + d.score, 0) / departmentScores.length)
    : 0;
  const topPerformer = departmentScores.length > 0 ? departmentScores[0] : null;
  const needsAttention = departmentScores.filter(d => d.score < 50).length;

  return (
    <DashboardLayout currentRoute="DepartmentScores">
      <View style={desktopStyles.container}>
        {/* Header Section */}
        <View style={desktopStyles.headerSection}>
          <View style={desktopStyles.headerLeft}>
            <Text style={desktopStyles.pageTitle}>Department Scores</Text>
            <Text style={desktopStyles.pageSubtitle}>
              ESG performance by department for {selectedQuarter}
            </Text>
          </View>

          {/* Quarter Selector */}
          <View style={desktopStyles.quarterSelector}>
            {["Q1", "Q2", "Q3", "Q4"].map((q) => (
              <TouchableOpacity
                key={q}
                style={[
                  desktopStyles.quarterBtn,
                  selectedQuarter === q && desktopStyles.quarterBtnActive,
                  hoveredQuarter === q && selectedQuarter !== q && desktopStyles.quarterBtnHover,
                ]}
                onPress={() => setSelectedQuarter(q)}
                onMouseEnter={() => setHoveredQuarter(q)}
                onMouseLeave={() => setHoveredQuarter(null)}
              >
                <Text
                  style={[
                    desktopStyles.quarterBtnText,
                    selectedQuarter === q && desktopStyles.quarterBtnTextActive,
                  ]}
                >
                  {q}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>

        {/* Summary Stats */}
        <View style={desktopStyles.statsRow}>
          <View style={desktopStyles.statCard}>
            <Text style={desktopStyles.statValue}>{departmentScores.length}</Text>
            <Text style={desktopStyles.statLabel}>Departments</Text>
          </View>
          <View style={desktopStyles.statCard}>
            <Text style={[desktopStyles.statValue, { color: getThemeScoreColor(avgScore) }]}>
              {avgScore}%
            </Text>
            <Text style={desktopStyles.statLabel}>Average Score</Text>
          </View>
          <View style={desktopStyles.statCard}>
            <Text style={[desktopStyles.statValue, { color: DesktopTheme.colors.success }]}>
              {topPerformer?.department || '-'}
            </Text>
            <Text style={desktopStyles.statLabel}>Top Performer</Text>
          </View>
          <View style={desktopStyles.statCard}>
            <Text style={[desktopStyles.statValue, { color: DesktopTheme.colors.error }]}>
              {needsAttention}
            </Text>
            <Text style={desktopStyles.statLabel}>Need Attention</Text>
          </View>
        </View>

        {/* Content Area */}
        {loading ? (
          <View style={desktopStyles.loadingContainer}>
            <ActivityIndicator size="large" color={DesktopTheme.colors.primary} />
            <Text style={desktopStyles.loadingText}>Loading department scores...</Text>
          </View>
        ) : departmentScores.length === 0 ? (
          <View style={desktopStyles.emptyContainer}>
            <Text style={desktopStyles.emptyIcon}>📊</Text>
            <Text style={desktopStyles.emptyTitle}>No Data Available</Text>
            <Text style={desktopStyles.emptyText}>
              No department scores found for {selectedQuarter}.
              Please ensure questionnaires have been submitted.
            </Text>
          </View>
        ) : (
          <View style={desktopStyles.cardsGrid}>
            {departmentScores.map((item, index) => (
              <DesktopGaugeCard
                key={index}
                department={item.department}
                score={item.score}
                isHovered={hoveredCard === index}
                onHoverIn={() => setHoveredCard(index)}
                onHoverOut={() => setHoveredCard(null)}
                onPress={() => onDepartmentPress(item.department)}
              />
            ))}
          </View>
        )}
      </View>
    </DashboardLayout>
  );
};

// Desktop Styles
const desktopStyles = StyleSheet.create({
  container: {
    flex: 1,
    padding: DesktopTheme.spacing.lg,
    backgroundColor: DesktopTheme.colors.contentBg,
  },
  headerSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: DesktopTheme.spacing.lg,
  },
  headerLeft: {
    flex: 1,
  },
  pageTitle: {
    fontSize: DesktopTheme.typography.fontSize.h2,
    fontFamily: DesktopTheme.typography.fontFamily.heading,
    color: DesktopTheme.colors.textPrimary,
    marginBottom: 4,
  },
  pageSubtitle: {
    fontSize: DesktopTheme.typography.fontSize.body,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    color: DesktopTheme.colors.textSecondary,
  },
  quarterSelector: {
    flexDirection: 'row',
    backgroundColor: DesktopTheme.colors.cardBg,
    borderRadius: DesktopTheme.borderRadius.lg,
    padding: 4,
    ...DesktopTheme.shadows.sm,
  },
  quarterBtn: {
    paddingVertical: DesktopTheme.spacing.sm,
    paddingHorizontal: DesktopTheme.spacing.lg,
    borderRadius: DesktopTheme.borderRadius.md,
  },
  quarterBtnActive: {
    backgroundColor: DesktopTheme.colors.primary,
  },
  quarterBtnHover: {
    backgroundColor: DesktopTheme.colors.border,
  },
  quarterBtnText: {
    fontSize: DesktopTheme.typography.fontSize.body,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    fontWeight: '600',
    color: DesktopTheme.colors.textSecondary,
  },
  quarterBtnTextActive: {
    color: '#ffffff',
  },
  statsRow: {
    flexDirection: 'row',
    gap: DesktopTheme.spacing.md,
    marginBottom: DesktopTheme.spacing.lg,
  },
  statCard: {
    flex: 1,
    backgroundColor: DesktopTheme.colors.cardBg,
    borderRadius: DesktopTheme.borderRadius.lg,
    padding: DesktopTheme.spacing.md,
    alignItems: 'center',
    ...DesktopTheme.shadows.sm,
  },
  statValue: {
    fontSize: DesktopTheme.typography.fontSize.h3,
    fontFamily: DesktopTheme.typography.fontFamily.heading,
    color: DesktopTheme.colors.textPrimary,
    marginBottom: 4,
  },
  statLabel: {
    fontSize: DesktopTheme.typography.fontSize.small,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    color: DesktopTheme.colors.textMuted,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 100,
  },
  loadingText: {
    marginTop: DesktopTheme.spacing.md,
    fontSize: DesktopTheme.typography.fontSize.body,
    color: DesktopTheme.colors.textSecondary,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 80,
    backgroundColor: DesktopTheme.colors.cardBg,
    borderRadius: DesktopTheme.borderRadius.lg,
    ...DesktopTheme.shadows.sm,
  },
  emptyIcon: {
    fontSize: 48,
    marginBottom: DesktopTheme.spacing.md,
  },
  emptyTitle: {
    fontSize: DesktopTheme.typography.fontSize.h3,
    fontFamily: DesktopTheme.typography.fontFamily.heading,
    color: DesktopTheme.colors.textPrimary,
    marginBottom: DesktopTheme.spacing.sm,
  },
  emptyText: {
    fontSize: DesktopTheme.typography.fontSize.body,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    color: DesktopTheme.colors.textSecondary,
    textAlign: 'center',
    maxWidth: 400,
  },
  cardsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: DesktopTheme.spacing.lg,
  },
  gaugeCard: {
    width: 220,
    backgroundColor: DesktopTheme.colors.cardBg,
    borderRadius: DesktopTheme.borderRadius.lg,
    padding: DesktopTheme.spacing.lg,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: DesktopTheme.colors.border,
    overflow: 'hidden',
    cursor: 'pointer',
    ...DesktopTheme.shadows.card,
    transition: 'all 0.2s ease',
  },
  gaugeCardHover: {
    transform: [{ translateY: -6 }],
    borderColor: DesktopTheme.colors.primary,
    ...DesktopTheme.shadows.lg,
  },
  gaugeAccent: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    opacity: 0.7,
  },
  gaugeWrapper: {
    marginBottom: DesktopTheme.spacing.md,
  },
  scoreCenter: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  scoreValue: {
    fontSize: DesktopTheme.typography.fontSize.h2,
    fontFamily: DesktopTheme.typography.fontFamily.heading,
    fontWeight: '700',
  },
  deptName: {
    fontSize: DesktopTheme.typography.fontSize.body,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    fontWeight: '600',
    color: DesktopTheme.colors.textPrimary,
    textAlign: 'center',
    marginBottom: DesktopTheme.spacing.sm,
    height: 40,
  },
  scoreBadge: {
    paddingVertical: 4,
    paddingHorizontal: DesktopTheme.spacing.sm,
    borderRadius: DesktopTheme.borderRadius.full,
  },
  scoreBadgeText: {
    fontSize: DesktopTheme.typography.fontSize.tiny,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    fontWeight: '600',
  },
});
const DepartmentTopicsModal = ({
  visible,
  department,
  topics,
  selectedQuarter,
  onClose,
  onTopicPress,
  isDesktopWeb,
}) => {
  const [hoveredTopic, setHoveredTopic] = useState(null);

  if (!visible) return null;

  const headerColors = isDesktopWeb
    ? [DesktopTheme.colors.primary, DesktopTheme.colors.primaryHover]
    : GlobalStyles.btnColor.gradientColors;

  const averageScore =
    topics.length > 0
      ? Math.round(
          topics.reduce((sum, t) => sum + (t.percent || 0), 0) / topics.length
        )
      : 0;

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={modalStyles.overlay}>
        <View style={[modalStyles.modalCard, isDesktopWeb && modalStyles.modalCardDesktop]}>
          <LinearGradient colors={headerColors} style={modalStyles.modalHeader}>
            <View style={modalStyles.modalHeaderLeft}>
              <View style={modalStyles.headerIcon}>
                <Ionicons name="business-outline" size={18} color="#ffffff" />
              </View>
              <View style={modalStyles.headerText}>
                <Text style={modalStyles.modalTitle}>{department || "Department"}</Text>
                <Text style={modalStyles.modalSubtitle}>Topics for {selectedQuarter}</Text>
              </View>
            </View>
            <TouchableOpacity style={modalStyles.modalCloseBtn} onPress={onClose}>
              <Ionicons name="close" size={20} color="#ffffff" />
            </TouchableOpacity>
          </LinearGradient>

          <View style={modalStyles.modalBody}>
            <View style={modalStyles.modalStatsRow}>
              <View style={modalStyles.modalStatCard}>
                <Text style={modalStyles.modalStatValue}>{topics.length}</Text>
                <Text style={modalStyles.modalStatLabel}>Topics</Text>
              </View>
              <View style={modalStyles.modalStatCard}>
                <Text
                  style={[
                    modalStyles.modalStatValue,
                    { color: getThemeScoreColor(averageScore) },
                  ]}
                >
                  {averageScore}%
                </Text>
                <Text style={modalStyles.modalStatLabel}>Avg Score</Text>
              </View>
            </View>

            {topics.length === 0 ? (
              <View style={modalStyles.emptyState}>
                <Ionicons
                  name="information-circle-outline"
                  size={24}
                  color={DesktopTheme.colors.textMuted}
                />
                <Text style={modalStyles.emptyStateText}>
                  No topics submitted for this quarter.
                </Text>
              </View>
            ) : (
              <ScrollView
                showsVerticalScrollIndicator={true}
                style={modalStyles.modalScroll}
                contentContainerStyle={modalStyles.modalScrollContent}
              >
                <View style={[modalStyles.topicGrid, !isDesktopWeb && modalStyles.topicList]}>
                  {topics.map((topicItem, index) => {
                    const scoreColor = getThemeScoreColor(topicItem.percent || 0);
                    const itemKey = [
                      topicItem.topic,
                      topicItem.employeeIdNum,
                      topicItem.responseId,
                      index,
                    ]
                      .filter(Boolean)
                      .join("-");
                    return (
                      <TouchableOpacity
                        key={itemKey}
                        style={[
                          modalStyles.topicCard,
                          isDesktopWeb && modalStyles.topicCardDesktop,
                          hoveredTopic === itemKey && isDesktopWeb && modalStyles.topicCardHover,
                        ]}
                        onPress={() => onTopicPress(topicItem)}
                        onMouseEnter={() => isDesktopWeb && setHoveredTopic(itemKey)}
                        onMouseLeave={() => isDesktopWeb && setHoveredTopic(null)}
                        activeOpacity={0.92}
                      >
                        <View style={modalStyles.topicCardHeader}>
                          <Text style={modalStyles.topicTitle} numberOfLines={2}>
                            {topicItem.topic}
                          </Text>
                          {topicItem.maxScore ? (
                            <View
                              style={[
                                modalStyles.topicScoreBadge,
                                {
                                  borderColor: scoreColor,
                                  backgroundColor: `${scoreColor}15`,
                                },
                              ]}
                            >
                              <Ionicons
                                name="stats-chart-outline"
                                size={12}
                                color={scoreColor}
                              />
                              <Text style={[modalStyles.topicScoreText, { color: scoreColor }]}>
                                {topicItem.totalScore} / {topicItem.maxScore}
                              </Text>
                            </View>
                          ) : (
                            <View style={modalStyles.topicScoreBadge}>
                              <Text style={modalStyles.topicScoreTextMuted}>Score N/A</Text>
                            </View>
                          )}
                        </View>

                        <View style={modalStyles.topicMetaRow}>
                          {topicItem.userName ? (
                            <View style={modalStyles.metaPill}>
                              <Ionicons
                                name="person-outline"
                                size={12}
                                color={DesktopTheme.colors.textSecondary}
                              />
                              <Text style={modalStyles.metaPillText}>{topicItem.userName}</Text>
                            </View>
                          ) : null}
                          {topicItem.pillar ? (
                            <View style={modalStyles.metaPill}>
                              <Ionicons
                                name="leaf-outline"
                                size={12}
                                color={DesktopTheme.colors.textSecondary}
                              />
                              <Text style={modalStyles.metaPillText}>{topicItem.pillar}</Text>
                            </View>
                          ) : null}
                        </View>

                        <View style={modalStyles.progressRow}>
                          <View style={modalStyles.progressTrack}>
                            <View
                              style={[
                                modalStyles.progressFill,
                                {
                                  width: `${topicItem.percent || 0}%`,
                                  backgroundColor: scoreColor,
                                },
                              ]}
                            />
                          </View>
                          <View style={modalStyles.progressMeta}>
                            <Text style={[modalStyles.progressText, { color: scoreColor }]}>
                              {topicItem.percent || 0}%
                            </Text>
                            <Ionicons
                              name="chevron-forward"
                              size={14}
                              color={DesktopTheme.colors.textMuted}
                            />
                          </View>
                        </View>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              </ScrollView>
            )}
          </View>
        </View>
      </View>
    </Modal>
  );
};

const TopicDetailsModal = ({ visible, topicData, onClose, isDesktopWeb }) => {
  if (!visible || !topicData) return null;

  const questionCountFromField = Number(topicData.NumberOfQuestions);
  const questionCount =
    Number.isFinite(questionCountFromField) && questionCountFromField > 0
      ? questionCountFromField
      : Array.isArray(topicData.responses)
        ? topicData.responses.length
        : 0;
  const maxScore = topicData.maxScore || (questionCount ? questionCount * 5 : 0);
  const totalScore = Number.isFinite(Number(topicData.totalScore))
    ? Number(topicData.totalScore)
    : 0;
  const percent = maxScore ? Math.round((totalScore / maxScore) * 100) : 0;
  const scoreColor = getThemeScoreColor(percent);
  const timestamp = topicData.timestamp?.toDate
    ? topicData.timestamp.toDate()
    : topicData.timestamp;
  const formattedDate = timestamp ? new Date(timestamp).toLocaleString() : "N/A";

  const headerColors = isDesktopWeb
    ? [DesktopTheme.colors.primary, DesktopTheme.colors.primaryHover]
    : GlobalStyles.btnColor.gradientColors;

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={modalStyles.overlay}>
        <View style={[modalStyles.modalCard, isDesktopWeb && modalStyles.modalCardDesktop]}>
          <LinearGradient colors={headerColors} style={modalStyles.modalHeader}>
            <View style={modalStyles.modalHeaderLeft}>
              <View style={modalStyles.headerIcon}>
                <Ionicons name="stats-chart-outline" size={18} color="#ffffff" />
              </View>
              <View style={modalStyles.headerText}>
                <Text style={modalStyles.modalTitle} numberOfLines={2}>
                  {topicData.topic || "Topic Details"}
                </Text>
                <Text style={modalStyles.modalSubtitle}>{topicData.department}</Text>
              </View>
            </View>
            <TouchableOpacity style={modalStyles.modalCloseBtn} onPress={onClose}>
              <Ionicons name="close" size={20} color="#ffffff" />
            </TouchableOpacity>
          </LinearGradient>

          <View style={modalStyles.modalBody}>
            <View style={modalStyles.detailMetaRow}>
              {topicData.userName ? (
                <View style={modalStyles.metaPill}>
                  <Ionicons
                    name="person-outline"
                    size={12}
                    color={DesktopTheme.colors.textSecondary}
                  />
                  <Text style={modalStyles.metaPillText}>{topicData.userName}</Text>
                </View>
              ) : null}
              {topicData.employeeIdNum ? (
                <View style={modalStyles.metaPill}>
                  <Ionicons
                    name="id-card-outline"
                    size={12}
                    color={DesktopTheme.colors.textSecondary}
                  />
                  <Text style={modalStyles.metaPillText}>ID {topicData.employeeIdNum}</Text>
                </View>
              ) : null}
              {topicData.quarter ? (
                <View style={modalStyles.metaPill}>
                  <Ionicons
                    name="calendar-outline"
                    size={12}
                    color={DesktopTheme.colors.textSecondary}
                  />
                  <Text style={modalStyles.metaPillText}>{topicData.quarter}</Text>
                </View>
              ) : null}
              {topicData.pillar ? (
                <View style={modalStyles.metaPill}>
                  <Ionicons
                    name="leaf-outline"
                    size={12}
                    color={DesktopTheme.colors.textSecondary}
                  />
                  <Text style={modalStyles.metaPillText}>{topicData.pillar}</Text>
                </View>
              ) : null}
            </View>

            <View style={modalStyles.scoreSummary}>
              <View style={modalStyles.scoreBlock}>
                <Text style={modalStyles.scoreLabel}>Total Score</Text>
                <Text style={[modalStyles.scoreValue, { color: scoreColor }]}>
                  {totalScore} / {maxScore || "N/A"}
                </Text>
                <Text style={modalStyles.scoreSubtext}>{formattedDate}</Text>
              </View>
              <View
                style={[
                  modalStyles.percentBadge,
                  { borderColor: scoreColor, backgroundColor: `${scoreColor}15` },
                ]}
              >
                <Text style={[modalStyles.percentBadgeText, { color: scoreColor }]}>
                  {percent}%
                </Text>
              </View>
            </View>

            <ScrollView
              showsVerticalScrollIndicator={true}
              style={modalStyles.modalScroll}
              contentContainerStyle={modalStyles.modalScrollContent}
            >
              {Array.isArray(topicData.responses) && topicData.responses.length > 0 ? (
                topicData.responses.map((item, index) => (
                  <View key={index} style={modalStyles.questionCard}>
                    <View style={modalStyles.questionHeader}>
                      <View
                        style={[
                          modalStyles.questionNumber,
                          { backgroundColor: `${scoreColor}18` },
                        ]}
                      >
                        <Text style={[modalStyles.questionNumberText, { color: scoreColor }]}>
                          {index + 1}
                        </Text>
                      </View>
                      <Text style={modalStyles.questionText}>{item.question}</Text>
                    </View>
                    <View style={modalStyles.answerRow}>
                      <Ionicons name="checkmark-circle-outline" size={16} color={scoreColor} />
                      <Text style={modalStyles.answerText}>
                        {item.selectedOption || "No answer provided"}
                      </Text>
                    </View>
                    {item.weight !== undefined && item.weight !== null ? (
                      <View
                        style={[
                          modalStyles.weightBadge,
                          { borderColor: scoreColor, backgroundColor: `${scoreColor}10` },
                        ]}
                      >
                        <Text style={[modalStyles.weightText, { color: scoreColor }]}>
                          Score: {item.weight}
                        </Text>
                      </View>
                    ) : null}
                    {item.remark ? (
                      <Text style={modalStyles.remarkText}>{item.remark}</Text>
                    ) : null}
                  </View>
                ))
              ) : (
                <View style={modalStyles.emptyState}>
                  <Text style={modalStyles.emptyStateText}>No responses recorded.</Text>
                </View>
              )}
            </ScrollView>
          </View>
        </View>
      </View>
    </Modal>
  );
};

// NEW: A circular gauge card component
const CircularGaugeCard = ({ department, score, onPress }) => {
  const getScoreColor = (percentage) => {
    if (percentage < 40) return "#d9534f"; // Red
    if (percentage < 75) return "#f0ad4e"; // Yellow
    return "#5cb85c"; // Green
  };

  return (
    <TouchableOpacity
      style={styles.cardContainer}
      onPress={onPress}
      activeOpacity={0.9}
    >
      <LinearGradient
        colors={["#ffffff", "#f5fbf7", "#eef7f1"]}
        style={styles.cardGradient}
      >
        <View style={[styles.cardAccent, { backgroundColor: getScoreColor(score) }]} />
        <AnimatedCircularProgress
          size={Platform.OS === "web" ? 120 : wp("24%")} // Responsive size
          width={Platform.OS === "web" ? 10 : wp("3%")} // Responsive thickness
          fill={score}
          tintColor={getScoreColor(score)}
          backgroundColor="#E0E0E0"
          arcSweepAngle={270}
          rotation={225}
          lineCap="round"
        >
          {(fill) => <Text style={styles.scoreText}>{Math.round(fill)}%</Text>}
        </AnimatedCircularProgress>
        <Text style={styles.departmentName} numberOfLines={1}>
          {department}
        </Text>
        <View style={[styles.scorePill, { borderColor: getScoreColor(score) }]}>
          <Text style={[styles.scorePillText, { color: getScoreColor(score) }]}>
            Tap for details
          </Text>
        </View>
      </LinearGradient>
    </TouchableOpacity>
  );
};

// Main Screen Component
export default function DepartmentScoresScreen() {
  const { companyId } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [departmentScores, setDepartmentScores] = useState([]);
  const [allResponses, setAllResponses] = useState([]);
  const isDesktopWeb = useDesktopDetect();
  const [departmentModalVisible, setDepartmentModalVisible] = useState(false);
  const [topicModalVisible, setTopicModalVisible] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [departmentTopics, setDepartmentTopics] = useState([]);
  const [selectedTopicDetails, setSelectedTopicDetails] = useState(null);

  const getCurrentQuarter = () => {
    const month = new Date().getMonth(); // 0-11
    if (month < 3) return "Q1";
    if (month < 6) return "Q2";
    if (month < 9) return "Q3";
    return "Q4";
  };

  const [selectedQuarter, setSelectedQuarter] = useState(getCurrentQuarter());

  const getDepartmentTopics = useCallback(
    (department) => {
      if (!department) return [];
      return allResponses
        .filter(
          (res) => res.quarter === selectedQuarter && res.department === department
        )
        .map((res) => {
          const questionCountFromField = Number(res.NumberOfQuestions);
          const questionCount =
            Number.isFinite(questionCountFromField) && questionCountFromField > 0
              ? questionCountFromField
              : Array.isArray(res.responses)
                ? res.responses.length
                : 0;
          const maxScore = questionCount ? questionCount * 5 : null;
          const totalScoreValue = Number(res.totalScore);
          const totalScore = Number.isFinite(totalScoreValue) ? totalScoreValue : 0;
          const percent = maxScore ? Math.round((totalScore / maxScore) * 100) : 0;

          return {
            ...res,
            totalScore,
            maxScore,
            percent,
          };
        })
        .sort((a, b) => {
          const diff = (b.percent || 0) - (a.percent || 0);
          if (diff !== 0) return diff;
          return (a.topic || "").localeCompare(b.topic || "");
        });
    },
    [allResponses, selectedQuarter]
  );

  const handleDepartmentPress = useCallback(
    (department) => {
      const topics = getDepartmentTopics(department);
      setSelectedDepartment(department);
      setDepartmentTopics(topics);
      setDepartmentModalVisible(true);
    },
    [getDepartmentTopics]
  );

  const handleTopicPress = useCallback((topicData) => {
    setSelectedTopicDetails(topicData);
    setTopicModalVisible(true);
  }, []);

  const handleCloseDepartmentModal = useCallback(() => {
    setDepartmentModalVisible(false);
    setSelectedDepartment(null);
    setDepartmentTopics([]);
    setTopicModalVisible(false);
    setSelectedTopicDetails(null);
  }, []);

  const handleCloseTopicModal = useCallback(() => {
    setTopicModalVisible(false);
    setSelectedTopicDetails(null);
  }, []);

  const fetchAllResponses = async () => {
    if (!companyId) return;
    try {
      const responsesRef = collection(
        db,
        `companies/${companyId}/questionnaireResponses`
      );
      const q = query(responsesRef);
      const snapshot = await getDocs(q);
      const responses = snapshot.docs.map((doc) => doc.data());
      setAllResponses(responses);
    } catch (error) {
      console.error("Error fetching department responses:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const calculateScoresForQuarter = (quarter) => {
    const quarterResponses = allResponses.filter(
      (res) => res.quarter === quarter
    );
    const groupedByDept = quarterResponses.reduce((acc, current) => {
      const { department, totalScore, NumberOfQuestions } = current;
      if (!department || !totalScore || !NumberOfQuestions) {
        return acc;
      }
      if (!acc[department]) {
        acc[department] = [];
      }
      const maxScore = NumberOfQuestions * 5;
      const percentage = (totalScore / maxScore) * 100;
      acc[department].push(percentage);
      return acc;
    }, {});

    const finalScores = Object.entries(groupedByDept).map(
      ([department, percentages]) => {
        const averageScore =
          percentages.reduce((sum, p) => sum + p, 0) / percentages.length;
        return {
          department,
          score: Math.round(averageScore),
        };
      }
    );
    finalScores.sort((a, b) => b.score - a.score);
    setDepartmentScores(finalScores);
  };

  useEffect(() => {
    fetchAllResponses();
  }, [companyId]);

  useEffect(() => {
    if (allResponses.length > 0) {
      setLoading(true);
      calculateScoresForQuarter(selectedQuarter);
      setLoading(false);
    } else {
      // If there are no responses at all, ensure we stop loading.
      setLoading(false);
      setDepartmentScores([]);
    }
  }, [selectedQuarter, allResponses]);

  useEffect(() => {
    if (departmentModalVisible && selectedDepartment) {
      setDepartmentTopics(getDepartmentTopics(selectedDepartment));
    }
  }, [
    allResponses,
    selectedQuarter,
    departmentModalVisible,
    selectedDepartment,
    getDepartmentTopics,
  ]);

  useFocusEffect(
    useCallback(() => {
      fetchAllResponses();
    }, [companyId])
  );

  const onRefresh = useCallback(() => {
    setRefreshing(true);
    fetchAllResponses();
  }, [companyId]);

  const renderContent = () => {
    if (loading) {
      return (
        <ActivityIndicator
          size="large"
          color="#007B55"
          style={{ marginTop: 50 }}
        />
      );
    }
    if (departmentScores.length === 0) {
      return (
        <View style={styles.centeredMessage}>
          <Text style={styles.messageText}>
            No data available for {selectedQuarter}.
          </Text>
        </View>
      );
    }
    return departmentScores.map((item, index) => (
      <CircularGaugeCard
        key={index}
        department={item.department}
        score={item.score}
        onPress={() => handleDepartmentPress(item.department)}
      />
    ));
  };

  // Desktop Web View
  if (isDesktopWeb) {
    return (
      <>
        <DesktopDepartmentScoresView
          loading={loading}
          departmentScores={departmentScores}
          selectedQuarter={selectedQuarter}
          setSelectedQuarter={setSelectedQuarter}
          onRefresh={onRefresh}
          onDepartmentPress={handleDepartmentPress}
        />
        <DepartmentTopicsModal
          visible={departmentModalVisible}
          department={selectedDepartment}
          topics={departmentTopics}
          selectedQuarter={selectedQuarter}
          onClose={handleCloseDepartmentModal}
          onTopicPress={handleTopicPress}
          isDesktopWeb
        />
        <TopicDetailsModal
          visible={topicModalVisible}
          topicData={selectedTopicDetails}
          onClose={handleCloseTopicModal}
          isDesktopWeb
        />
      </>
    );
  }

  // Mobile View
  return (
    <>
      <LinearGradient // Use LinearGradient for the background
        colors={GlobalStyles.GlobalBg.gradientColors}
        style={{flex: 1}}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
      >
        <ScrollView
          style={styles.screenContainer}
          contentContainerStyle={{
            paddingBottom: 30,
            alignContent: "center",
            alignItems: "center",
          }}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
          }
        >
          <View style={styles.quarterSelectorContainer}>
            {["Q1", "Q2", "Q3", "Q4"].map((q) => (
              <TouchableOpacity
                key={q}
                style={[
                  styles.quarterButton,
                  selectedQuarter === q && styles.quarterButtonSelected,
                ]}
                onPress={() => setSelectedQuarter(q)}
              >
                <Text
                  style={[
                    styles.quarterButtonText,
                    selectedQuarter === q && styles.quarterButtonTextSelected,
                  ]}
                >
                  {q}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          <View style={styles.contentContainer}>{renderContent()}</View>
        </ScrollView>
      </LinearGradient>
      <DepartmentTopicsModal
        visible={departmentModalVisible}
        department={selectedDepartment}
        topics={departmentTopics}
        selectedQuarter={selectedQuarter}
        onClose={handleCloseDepartmentModal}
        onTopicPress={handleTopicPress}
        isDesktopWeb={false}
      />
      <TopicDetailsModal
        visible={topicModalVisible}
        topicData={selectedTopicDetails}
        onClose={handleCloseTopicModal}
        isDesktopWeb={false}
      />
    </>
  );
}

// --- Styles ---
const styles = StyleSheet.create({
  screenContainer: {
    flex: 1,
  },
  quarterSelectorContainer: {
    flexDirection: "row",
    justifyContent: "space-around",
    paddingVertical: hp("1%"),
    backgroundColor: "white",
    width: "90%",
    borderRadius: 50,
    marginTop: hp("2%"),
    marginBottom: hp("3%"),
    elevation: 4,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  quarterButton: {
    paddingVertical: hp("1%"),
    paddingHorizontal: wp("5%"),
    borderRadius: 20,
  },
  quarterButtonSelected: {
    backgroundColor: GlobalStyles.btnColor.backgroundColor,
  },
  quarterButtonText: {
    fontSize: GlobalStyles.fontSize.md,
    fontFamily: GlobalStyles.GlobalFont.fontFamily,
    color: "#555",
    fontWeight: "600",
  },
  quarterButtonTextSelected: {
    color: "white",
    fontWeight: "bold",
  },
  contentContainer: {
    flexDirection: "row", // Enable horizontal layout
    flexWrap: "wrap", // Allow items to wrap to the next line
    justifyContent: "flex-start", // Align items from the start
    paddingHorizontal: wp("2%"),
  },
  centeredMessage: {
    width: "100%",
    marginTop: hp("10%"),
    alignItems: "center",
  },
  messageText: {
    fontSize: GlobalStyles.fontSize.md,
    color: "#888",
    fontFamily: GlobalStyles.GlobalFont.fontFamily,
  },
  // --- CircularGaugeCard Styles ---
  cardContainer: {
    width: wp("22%"), // ~4 items per row with small gap
    margin: 10,
    backgroundColor: "white",
    borderRadius: 16,
    overflow: "hidden",
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: "rgba(15, 23, 42, 0.08)",
    elevation: 3,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 6,
  },
  cardGradient: {
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: hp("2%"),
    paddingHorizontal: wp("2%"),
  },
  cardAccent: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    opacity: 0.85,
  },
  departmentName: {
    fontSize: Platform.OS === "web" ? 14 : GlobalStyles.fontSize.sm,
    fontWeight: "600",
    color: "#34495e",
    fontFamily: GlobalStyles.GlobalFont.fontFamily,
    marginTop: hp("1.5%"),
    textAlign: "center",
    paddingHorizontal: 5,
  },
  scoreText: {
    fontSize: Platform.OS === "web" ? 20 : GlobalStyles.fontSize.lg,
    fontWeight: "bold",
    color: "#2c3e50",
    fontFamily: GlobalStyles.GlobalFont.fontFamily,
  },
  scorePill: {
    marginTop: hp("1%"),
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 999,
    borderWidth: 1,
    backgroundColor: "rgba(255, 255, 255, 0.85)",
  },
  scorePillText: {
    fontSize: GlobalStyles.fontSize.xss,
    fontFamily: GlobalStyles.inpFont.fontFamily,
    fontWeight: "600",
  },
});

const modalStyles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(15, 23, 42, 0.6)",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
  },
  modalCard: {
    width: "92%",
    maxHeight: "85%",
    backgroundColor: "#ffffff",
    borderRadius: 18,
    overflow: "hidden",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 12 },
    shadowOpacity: 0.18,
    shadowRadius: 20,
    elevation: 10,
  },
  modalCardDesktop: {
    width: 860,
    maxWidth: "92%",
    borderWidth: 1,
    borderColor: DesktopTheme.colors.border,
  },
  modalHeader: {
    paddingHorizontal: 20,
    paddingVertical: 16,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  modalHeaderLeft: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },
  headerIcon: {
    width: 36,
    height: 36,
    borderRadius: 10,
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
  },
  headerText: {
    flex: 1,
  },
  modalTitle: {
    fontSize: 18,
    fontFamily: DesktopTheme.typography.fontFamily.heading,
    color: "#ffffff",
  },
  modalSubtitle: {
    fontSize: 12,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    color: "rgba(255, 255, 255, 0.8)",
    marginTop: 2,
  },
  modalCloseBtn: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    alignItems: "center",
    justifyContent: "center",
  },
  modalBody: {
    padding: 20,
    backgroundColor: "#ffffff",
    flex: 1,
    minHeight: 0,
  },
  modalScroll: {
    flex: 1,
    minHeight: 0,
  },
  modalScrollContent: {
    paddingBottom: 12,
  },
  modalStatsRow: {
    flexDirection: "row",
    gap: 12,
    marginBottom: 16,
  },
  modalStatCard: {
    flex: 1,
    padding: 12,
    borderRadius: 12,
    backgroundColor: "#f8fafc",
    borderWidth: 1,
    borderColor: "rgba(148, 163, 184, 0.2)",
    alignItems: "center",
  },
  modalStatValue: {
    fontSize: 18,
    fontFamily: DesktopTheme.typography.fontFamily.heading,
    color: DesktopTheme.colors.textPrimary,
  },
  modalStatLabel: {
    fontSize: 11,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    color: DesktopTheme.colors.textMuted,
    marginTop: 2,
  },
  emptyState: {
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 24,
  },
  emptyStateText: {
    marginTop: 8,
    fontSize: 12,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    color: DesktopTheme.colors.textMuted,
    textAlign: "center",
  },
  topicGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
  },
  topicList: {
    flexDirection: "column",
  },
  topicCard: {
    backgroundColor: "#ffffff",
    borderRadius: 14,
    padding: 14,
    borderWidth: 1,
    borderColor: "rgba(148, 163, 184, 0.2)",
    marginBottom: 14,
  },
  topicCardDesktop: {
    width: "48%",
    minWidth: 260,
    cursor: "pointer",
    transition: "all 0.2s ease",
  },
  topicCardHover: {
    transform: [{ translateY: -4 }],
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.12,
    shadowRadius: 16,
    elevation: 6,
    borderColor: "rgba(5, 150, 105, 0.35)",
  },
  topicCardHeader: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
  },
  topicTitle: {
    flex: 1,
    fontSize: 14,
    fontFamily: DesktopTheme.typography.fontFamily.heading,
    color: DesktopTheme.colors.textPrimary,
    marginRight: 8,
  },
  topicScoreBadge: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 999,
    borderWidth: 1,
  },
  topicScoreText: {
    marginLeft: 4,
    fontSize: 11,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    fontWeight: "600",
  },
  topicScoreTextMuted: {
    fontSize: 11,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    color: DesktopTheme.colors.textMuted,
  },
  topicMetaRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginTop: 10,
  },
  metaPill: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 999,
    backgroundColor: DesktopTheme.colors.contentBg,
    marginRight: 8,
    marginBottom: 8,
  },
  metaPillText: {
    marginLeft: 4,
    fontSize: 11,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    color: DesktopTheme.colors.textSecondary,
  },
  progressRow: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 8,
  },
  progressTrack: {
    flex: 1,
    height: 6,
    backgroundColor: DesktopTheme.colors.border,
    borderRadius: 999,
    overflow: "hidden",
  },
  progressFill: {
    height: "100%",
    borderRadius: 999,
  },
  progressMeta: {
    flexDirection: "row",
    alignItems: "center",
    marginLeft: 8,
  },
  progressText: {
    fontSize: 11,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    marginRight: 4,
  },
  detailMetaRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginBottom: 12,
  },
  scoreSummary: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "#f8fafc",
    borderRadius: 12,
    padding: 12,
    borderWidth: 1,
    borderColor: "rgba(148, 163, 184, 0.2)",
    marginBottom: 16,
  },
  scoreBlock: {
    flex: 1,
  },
  scoreLabel: {
    fontSize: 12,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    color: DesktopTheme.colors.textMuted,
  },
  scoreValue: {
    fontSize: 18,
    fontFamily: DesktopTheme.typography.fontFamily.heading,
    marginTop: 4,
  },
  scoreSubtext: {
    fontSize: 11,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    color: DesktopTheme.colors.textMuted,
    marginTop: 4,
  },
  percentBadge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 999,
    borderWidth: 1,
    marginLeft: 12,
  },
  percentBadgeText: {
    fontSize: 12,
    fontFamily: DesktopTheme.typography.fontFamily.heading,
  },
  questionCard: {
    backgroundColor: "#f8fafc",
    borderRadius: 12,
    padding: 12,
    borderWidth: 1,
    borderColor: "rgba(148, 163, 184, 0.2)",
    marginBottom: 12,
  },
  questionHeader: {
    flexDirection: "row",
    alignItems: "flex-start",
  },
  questionNumber: {
    width: 26,
    height: 26,
    borderRadius: 13,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 8,
  },
  questionNumberText: {
    fontSize: 12,
    fontFamily: DesktopTheme.typography.fontFamily.heading,
  },
  questionText: {
    flex: 1,
    fontSize: 13,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    color: DesktopTheme.colors.textPrimary,
    lineHeight: 18,
  },
  answerRow: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 8,
  },
  answerText: {
    marginLeft: 6,
    fontSize: 12,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    color: DesktopTheme.colors.textSecondary,
    flex: 1,
    lineHeight: 18,
  },
  weightBadge: {
    alignSelf: "flex-start",
    marginTop: 8,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 999,
    borderWidth: 1,
  },
  weightText: {
    fontSize: 11,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    fontWeight: "600",
  },
  remarkText: {
    marginTop: 8,
    fontSize: 11,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    color: DesktopTheme.colors.textMuted,
  },
});
